import { useState } from 'react';
import { BracketTeam, NCAARegion, BRACKET_MATCHUP_ORDER } from '../types/pool';

interface BracketVisualizationProps {
  teams: BracketTeam[];
  onTeamClick?: (team: BracketTeam) => void;
  showResults?: boolean;
  compact?: boolean;
}

// Team slot component for displaying a single team in a matchup
function TeamSlot({
  team,
  isWinner = false,
  isEliminated = false,
  onClick
}: {
  team?: BracketTeam;
  isWinner?: boolean;
  isEliminated?: boolean;
  onClick?: () => void;
}) {
  const isTBD = !team || team.name.includes('TBD');

  return (
    <div
      onClick={team && onClick ? onClick : undefined}
      className={`
        flex items-center gap-2 px-2 py-1.5 border rounded text-sm
        ${onClick && team ? 'cursor-pointer hover:bg-blue-50' : ''}
        ${isWinner ? 'bg-green-50 border-green-300 font-semibold' : 'border-gray-200'}
        ${isEliminated ? 'bg-gray-100 text-gray-400 line-through' : ''}
        ${isTBD ? 'bg-gray-50 text-gray-400 italic' : 'bg-white'}
      `}
    >
      {team ? (
        <>
          <span className={`
            w-6 h-6 flex items-center justify-center rounded text-xs font-bold
            ${isTBD ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white'}
          `}>
            {team.seed}
          </span>
          <span className="truncate flex-1 min-w-0">
            {isTBD ? `${team.region} #${team.seed}` : team.name}
          </span>
        </>
      ) : (
        <span className="text-gray-400 italic">TBD</span>
      )}
    </div>
  );
}

// Matchup component showing two teams facing each other
function Matchup({
  team1,
  team2,
  onTeamClick
}: {
  team1?: BracketTeam;
  team2?: BracketTeam;
  onTeamClick?: (team: BracketTeam) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <TeamSlot
        team={team1}
        onClick={team1 ? () => onTeamClick?.(team1) : undefined}
        isEliminated={team1?.eliminated}
      />
      <TeamSlot
        team={team2}
        onClick={team2 ? () => onTeamClick?.(team2) : undefined}
        isEliminated={team2?.eliminated}
      />
    </div>
  );
}

// Region bracket component showing one quarter of the bracket
function RegionBracket({
  region,
  teams,
  onTeamClick,
  compact = false
}: {
  region: NCAARegion;
  teams: BracketTeam[];
  onTeamClick?: (team: BracketTeam) => void;
  compact?: boolean;
}) {
  // Get teams for this region sorted by seed
  const regionTeams = teams
    .filter(t => t.region === region)
    .sort((a, b) => a.seed - b.seed);

  // Build matchups based on standard bracket order
  const matchups = BRACKET_MATCHUP_ORDER.map(([seed1, seed2]) => ({
    team1: regionTeams.find(t => t.seed === seed1),
    team2: regionTeams.find(t => t.seed === seed2),
  }));

  const regionColors: Record<NCAARegion, string> = {
    'East': 'bg-blue-600',
    'West': 'bg-red-600',
    'South': 'bg-green-600',
    'Midwest': 'bg-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className={`${regionColors[region]} text-white px-4 py-2 text-center font-bold`}>
        {region} Region
      </div>
      <div className={`p-3 ${compact ? 'space-y-2' : 'space-y-3'}`}>
        {matchups.map((m, idx) => (
          <Matchup
            key={idx}
            team1={m.team1}
            team2={m.team2}
            onTeamClick={onTeamClick}
          />
        ))}
      </div>
    </div>
  );
}

// Full bracket view with all four regions
export function BracketVisualization({
  teams,
  onTeamClick,
  showResults: _showResults = false, // Reserved for future use when showing game results
  compact = false
}: BracketVisualizationProps) {
  const [selectedRegion, setSelectedRegion] = useState<NCAARegion | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const regions: NCAARegion[] = ['East', 'West', 'South', 'Midwest'];

  // Check if teams are populated
  const hasTeams = teams.some(t => !t.name.includes('TBD'));

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">🏀</span>
            NCAA Tournament Bracket
          </h2>
          <div className="flex items-center gap-2">
            {/* Region Filter */}
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value as NCAARegion | 'all')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        {!hasTeams && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <span className="font-semibold">Awaiting Selection Sunday:</span> Teams will be announced on Selection Sunday (second Sunday in March). The bracket will be populated once the field is set.
          </div>
        )}
      </div>

      {/* Bracket Display */}
      <div className="p-4">
        {viewMode === 'grid' ? (
          <div className={`grid gap-4 ${selectedRegion === 'all' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-xl mx-auto'}`}>
            {(selectedRegion === 'all' ? regions : [selectedRegion]).map(region => (
              <RegionBracket
                key={region}
                region={region}
                teams={teams}
                onTeamClick={onTeamClick}
                compact={compact}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Region</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Seed</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Team</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Conference</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {teams
                  .filter(t => selectedRegion === 'all' || t.region === selectedRegion)
                  .sort((a, b) => {
                    if (a.region !== b.region) return a.region.localeCompare(b.region);
                    return a.seed - b.seed;
                  })
                  .map((team, idx) => {
                    const isTBD = team.name.includes('TBD');
                    return (
                      <tr
                        key={team.id}
                        className={`
                          ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          ${team.eliminated ? 'text-gray-400' : ''}
                          ${onTeamClick ? 'cursor-pointer hover:bg-blue-50' : ''}
                        `}
                        onClick={() => onTeamClick?.(team)}
                      >
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            team.region === 'East' ? 'bg-blue-100 text-blue-800' :
                            team.region === 'West' ? 'bg-red-100 text-red-800' :
                            team.region === 'South' ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {team.region}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-600 text-white text-xs font-bold">
                            {team.seed}
                          </span>
                        </td>
                        <td className={`px-3 py-2 ${isTBD ? 'text-gray-400 italic' : 'font-medium'} ${team.eliminated ? 'line-through' : ''}`}>
                          {isTBD ? 'To Be Determined' : team.name}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {team.conference === 'TBD' ? '-' : team.conference}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {team.eliminated ? (
                            <span className="text-red-600 text-xs font-medium">Eliminated</span>
                          ) : isTBD ? (
                            <span className="text-gray-400 text-xs">Pending</span>
                          ) : (
                            <span className="text-green-600 text-xs font-medium">Active</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 border-t border-gray-200 pt-3">
          <span className="font-semibold">Regions:</span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-blue-600"></span> East
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-600"></span> West
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-600"></span> South
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-orange-600"></span> Midwest
          </span>
        </div>
      </div>
    </div>
  );
}

export default BracketVisualization;
